<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <nav class="breadcrumb-nav mb-2">
                    <a href="/analytics" class="text-muted">Analytics</a>
                    <span class="mx-2 text-muted">/</span>
                    <a href="/analytics/validators" class="text-muted">Validators</a>
                    <span class="mx-2 text-muted">/</span>
                    <span class="text-gradient">{{ validatorName || formatPubkey(address) }}</span>
                </nav>
                <h1 class="mb-2">
                    <span class="text-gradient">{{ validatorName || 'Validator Analytics' }}</span>
                </h1>
                <p class="font-mono text-muted mb-3">
                    <a :href="`https://solscan.io/account/${address}`" target="_blank" class="text-gradient">
                        {{ address }}
                    </a>
                </p>
                <date-range-picker default-range="7d" @change="handleDateRangeChange" />
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Blocks Produced</div>
                <div class="hero-value">{{ formatCompact(summary.totalBlocks) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Total Revenue</div>
                <div class="hero-value">{{ formatSOL(summary.totalRevenue) }}</div>
                <div class="hero-subtitle">fees + tips</div>
            </div>
            <div class="analytics-hero-stat hero-stat--cyan">
                <div class="hero-label">Completion Rate</div>
                <div class="hero-value">{{ summary.completionRate || 0 }}%</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Transactions</div>
                <div class="hero-value">{{ formatCompact(summary.totalTxns) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--amber">
                <div class="hero-label">Jito Tips</div>
                <div class="hero-value">{{ formatSOL(summary.totalJitoTips) }}</div>
                <div class="hero-subtitle">{{ summary.jitoPercent || 0 }}% of txns</div>
            </div>
            <div class="analytics-hero-stat" v-if="defi.swapVolume > 0">
                <div class="hero-label">DeFi Exposure</div>
                <div class="hero-value">{{ defi.defiExposureScore || 0 }}</div>
                <div class="hero-subtitle">score out of 100</div>
            </div>
        </div>

        <!-- Tab Navigation -->
        <div class="validator-tabs mb-4">
            <div class="btn-group">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    class="compass-btn"
                    :class="{ 'compass-btn--primary': activeTab === tab.key }"
                    @click="switchTab(tab.key)"
                >
                    {{ tab.label }}
                </button>
            </div>
        </div>

        <!-- TAB: Performance -->
        <div v-show="activeTab === 'performance'">
            <div class="section-divider">
                <span class="section-title">Block Production & Revenue</span>
            </div>
            <div class="analytics-row cols-2 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Daily Blocks Produced</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="blocksChart" class="chart-canvas"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Revenue (Fees + Jito Tips)</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="revenueChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>

            <div class="analytics-row cols-2 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Transaction Volume</div>
                        <div class="panel-subtitle">Completed vs reverted</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="txChart" class="chart-canvas"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Completion Rate</div>
                        <div class="panel-subtitle">% of transactions successfully completed</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="completionChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>

            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Compute Units Consumed</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="cuChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Performance Summary Table -->
            <div class="section-divider">
                <span class="section-title">Performance Summary</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body">
                        <div class="metrics-table-grid">
                            <div class="metrics-table-section">
                                <div class="metrics-table-header">Production</div>
                                <table class="compass-table compact">
                                    <tbody>
                                        <tr><td>Total Blocks</td><td class="text-end font-mono">{{ formatCompact(summary.totalBlocks) }}</td></tr>
                                        <tr><td>Total Txns</td><td class="text-end font-mono">{{ formatCompact(summary.totalTxns) }}</td></tr>
                                        <tr><td>Avg Txns/Block</td><td class="text-end font-mono">{{ summary.avgTxnsPerBlock || 0 }}</td></tr>
                                        <tr><td>Completion Rate</td><td class="text-end font-mono">{{ summary.completionRate || 0 }}%</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="metrics-table-section">
                                <div class="metrics-table-header">Revenue</div>
                                <table class="compass-table compact">
                                    <tbody>
                                        <tr><td>Total Fees</td><td class="text-end font-mono">{{ formatSOL(summary.totalFees) }}</td></tr>
                                        <tr><td>Total Jito Tips</td><td class="text-end font-mono">{{ formatSOL(summary.totalJitoTips) }}</td></tr>
                                        <tr><td>Total Revenue</td><td class="text-end font-mono">{{ formatSOL(summary.totalRevenue) }}</td></tr>
                                        <tr><td>Jito % of Txns</td><td class="text-end font-mono">{{ summary.jitoPercent || 0 }}%</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div class="metrics-table-section">
                                <div class="metrics-table-header">Compute</div>
                                <table class="compass-table compact">
                                    <tbody>
                                        <tr><td>Total CU</td><td class="text-end font-mono">{{ formatCompute(summary.totalComputeUnits) }}</td></tr>
                                        <tr><td>Completed Txns</td><td class="text-end font-mono">{{ formatCompact(summary.completedTxns) }}</td></tr>
                                        <tr><td>Reverted Txns</td><td class="text-end font-mono">{{ formatCompact(summary.revertedTxns) }}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Time Series Table -->
            <div class="section-divider">
                <span class="section-title">Daily Breakdown</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Raw Data</div>
                        <div class="panel-controls">
                            <button class="compass-btn compass-btn--outline compass-btn--sm" @click="exportPerformanceCSV">Export CSV</button>
                        </div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table time-series-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th class="text-end">Blocks</th>
                                        <th class="text-end">Txns</th>
                                        <th class="text-end">Completion</th>
                                        <th class="text-end">Fees</th>
                                        <th class="text-end">Jito Tips</th>
                                        <th class="text-end">Revenue</th>
                                        <th class="text-end">CU</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="row in timeSeries" :key="row.date">
                                        <td>{{ row.date }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(row.blocks) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(row.txns) }}</td>
                                        <td class="text-end">
                                            <span :class="getCompletionClass(row.completionRate)">{{ row.completionRate }}%</span>
                                        </td>
                                        <td class="text-end font-mono">{{ row.fees.toFixed(4) }}</td>
                                        <td class="text-end font-mono" style="color: var(--compass-jito, #f97316);">{{ row.jitoTips.toFixed(4) }}</td>
                                        <td class="text-end font-mono" style="color: var(--compass-success, #22c55e);">{{ row.revenue.toFixed(4) }}</td>
                                        <td class="text-end font-mono">{{ formatCompute(row.computeUnits) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB: DeFi Activity -->
        <div v-show="activeTab === 'defi'">
            <div class="section-divider">
                <span class="section-title">DeFi Activity Overview</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body">
                        <div class="fee-metrics-grid">
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">Swap Volume</div>
                                <div class="fee-metric-value" style="color: #22c55e;">${{ formatCompact(defi.swapVolume) }}</div>
                                <div class="fee-metric-percent">total USD volume</div>
                            </div>
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">Swap Count</div>
                                <div class="fee-metric-value" style="color: #10f7cf;">{{ formatCompact(defi.swapCount) }}</div>
                                <div class="fee-metric-percent">total swaps processed</div>
                            </div>
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">Transfer Volume</div>
                                <div class="fee-metric-value" style="color: #8b5cf6;">${{ formatCompact(defi.transferVolume) }}</div>
                                <div class="fee-metric-percent">total transfer value</div>
                            </div>
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">Traders Served</div>
                                <div class="fee-metric-value" style="color: #ec4899;">{{ formatCompact(defi.tradersServed) }}</div>
                                <div class="fee-metric-percent">unique traders</div>
                            </div>
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">Avg Swap Size</div>
                                <div class="fee-metric-value" style="color: #f59e0b;">${{ formatCompact(defi.avgSwapSize) }}</div>
                                <div class="fee-metric-percent">per swap</div>
                            </div>
                            <div class="fee-metric-card">
                                <div class="fee-metric-header">DeFi Score</div>
                                <div class="fee-metric-value" style="color: #3b82f6;">{{ defi.defiExposureScore || 0 }}</div>
                                <div class="fee-metric-percent">out of 100</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="analytics-row cols-2 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Swap Volume Over Time</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="swapVolumeChart" class="chart-canvas"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Traders & Signers</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="tradersChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>

            <div class="analytics-row cols-2 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Swap Count Over Time</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="swapCountChart" class="chart-canvas"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Transfer Volume Over Time</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="transferVolumeChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB: Block Scheduling -->
        <div v-show="activeTab === 'scheduling'">
            <div class="section-divider">
                <span class="section-title">Tick-Level Block Scheduling</span>
            </div>
            <p class="text-muted mb-3">
                How this validator distributes transactions across the 64 tick positions within each slot.
                Brighter colours indicate higher compute consumption at that tick position.
            </p>

            <div class="analytics-row cols-1 mb-4" v-if="tickData.length > 0">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Compute Usage by Tick Position</div>
                        <div class="panel-subtitle">Average CU consumed per tick ({{ tickTotalSlots }} slots sampled)</div>
                    </div>
                    <div class="panel-body chart-extra-tall">
                        <canvas ref="tickHeatmapChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>
            <div v-else class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body text-center py-5 text-muted">
                        No tick scheduling data available for this validator in the selected time range.
                    </div>
                </div>
            </div>

            <div class="analytics-row cols-2 mb-4" v-if="tickData.length > 0">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Priority Fees by Tick</div>
                        <div class="panel-subtitle">Average priority fees per tick position (lamports)</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="tickFeesChart" class="chart-canvas"></canvas>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Non-Vote Transactions by Tick</div>
                        <div class="panel-subtitle">Average non-vote transaction count per tick</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="tickNonVotesChart" class="chart-canvas"></canvas>
                    </div>
                </div>
            </div>

            <!-- Tick Data Table -->
            <div class="analytics-row cols-1 mb-4" v-if="tickData.length > 0">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Tick Profile Data</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table compass-table--striped">
                                <thead>
                                    <tr>
                                        <th>Tick</th>
                                        <th class="text-end">Avg Non-Votes</th>
                                        <th class="text-end">Avg CU</th>
                                        <th class="text-end">Avg Priority Fees</th>
                                        <th class="text-end">Avg Jito Tips</th>
                                        <th class="text-end">Slots Sampled</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="tick in tickData" :key="tick.tick">
                                        <td class="font-mono">{{ tick.tick }}</td>
                                        <td class="text-end font-mono">{{ tick.avg_non_votes.toFixed(2) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(tick.avg_cu) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(tick.avg_priority_fees) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(tick.avg_jito_tips) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(tick.slot_count) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB: Recent Blocks -->
        <div v-show="activeTab === 'blocks'">
            <div class="section-divider">
                <span class="section-title">Recent Blocks</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Blocks Produced by This Validator</div>
                        <div class="panel-subtitle">{{ recentBlocks.length }} most recent blocks</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive" v-if="recentBlocks.length > 0">
                            <table class="compass-table compass-table--striped">
                                <thead>
                                    <tr>
                                        <th>Slot</th>
                                        <th>Time</th>
                                        <th class="text-end">Txns</th>
                                        <th class="text-end">Completion</th>
                                        <th class="text-end">CU</th>
                                        <th class="text-end">Fees</th>
                                        <th class="text-end">Jito Tips</th>
                                        <th class="text-end">Revenue</th>
                                        <th class="text-end">Swaps</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="block in recentBlocks" :key="block.slot">
                                        <td>
                                            <a :href="`/analytics/block/${block.slot}`" class="text-gradient font-mono">
                                                {{ block.slot.toLocaleString() }}
                                            </a>
                                        </td>
                                        <td class="text-muted">{{ formatBlockTime(block.timestamp) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(block.txns) }}</td>
                                        <td class="text-end">
                                            <span :class="getCompletionClass(block.completionRate)">{{ block.completionRate }}%</span>
                                        </td>
                                        <td class="text-end font-mono">{{ formatCompute(block.computeUnits) }}</td>
                                        <td class="text-end font-mono">{{ block.fees.toFixed(4) }}</td>
                                        <td class="text-end font-mono" style="color: var(--compass-jito, #f97316);">{{ block.jitoTips.toFixed(4) }}</td>
                                        <td class="text-end font-mono" style="color: var(--compass-success, #22c55e);">{{ block.revenue.toFixed(4) }}</td>
                                        <td class="text-end font-mono">{{ block.swaps > 0 ? formatCompact(block.swaps) : '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else class="text-center py-5 text-muted">
                            No recent blocks found for this validator.
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- TAB: Comparison -->
        <div v-show="activeTab === 'compare'">
            <div class="section-divider">
                <span class="section-title">Compare with Another Validator</span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-body">
                        <div class="compare-input-row mb-4">
                            <label class="text-muted me-2">Compare with:</label>
                            <input
                                v-model="compareAddress"
                                type="text"
                                class="form-control form-control-sm font-mono"
                                placeholder="Enter validator identity address..."
                                style="max-width: 500px; display: inline-block; background: var(--compass-bg-secondary, #1a1a2e); border-color: var(--compass-border, rgba(139,92,246,0.2)); color: #e5e7eb;"
                                @keyup.enter="loadComparison"
                            />
                            <button
                                class="compass-btn compass-btn--primary compass-btn--sm ms-2"
                                @click="loadComparison"
                                :disabled="!compareAddress || compareLoading"
                            >
                                {{ compareLoading ? 'Loading...' : 'Compare' }}
                            </button>
                        </div>

                        <div v-if="compareData" class="compare-grid">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Metric</th>
                                        <th class="text-end">{{ formatPubkey(address) }}</th>
                                        <th class="text-end">{{ formatPubkey(compareAddress) }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Blocks</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader1?.metrics?.blocks) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader2?.metrics?.blocks) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Transactions</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader1?.metrics?.txns) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader2?.metrics?.txns) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Completion Rate</td>
                                        <td class="text-end font-mono">{{ compareData.leader1?.metrics?.completionRate || 0 }}%</td>
                                        <td class="text-end font-mono">{{ compareData.leader2?.metrics?.completionRate || 0 }}%</td>
                                    </tr>
                                    <tr>
                                        <td>Fees (SOL)</td>
                                        <td class="text-end font-mono">{{ (compareData.leader1?.metrics?.fees || 0).toFixed(4) }}</td>
                                        <td class="text-end font-mono">{{ (compareData.leader2?.metrics?.fees || 0).toFixed(4) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Jito Tips (SOL)</td>
                                        <td class="text-end font-mono">{{ (compareData.leader1?.metrics?.jitoTips || 0).toFixed(4) }}</td>
                                        <td class="text-end font-mono">{{ (compareData.leader2?.metrics?.jitoTips || 0).toFixed(4) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Revenue (SOL)</td>
                                        <td class="text-end font-mono" style="color: var(--compass-success, #22c55e);">{{ (compareData.leader1?.metrics?.revenue || 0).toFixed(4) }}</td>
                                        <td class="text-end font-mono" style="color: var(--compass-success, #22c55e);">{{ (compareData.leader2?.metrics?.revenue || 0).toFixed(4) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Compute Units</td>
                                        <td class="text-end font-mono">{{ formatCompute(compareData.leader1?.metrics?.computeUnits) }}</td>
                                        <td class="text-end font-mono">{{ formatCompute(compareData.leader2?.metrics?.computeUnits) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Swap Volume (USD)</td>
                                        <td class="text-end font-mono">${{ formatCompact(compareData.leader1?.metrics?.swapVolume) }}</td>
                                        <td class="text-end font-mono">${{ formatCompact(compareData.leader2?.metrics?.swapVolume) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Traders</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader1?.metrics?.traders) }}</td>
                                        <td class="text-end font-mono">{{ formatCompact(compareData.leader2?.metrics?.traders) }}</td>
                                    </tr>
                                    <tr>
                                        <td>Avg Txns/Block</td>
                                        <td class="text-end font-mono">{{ (compareData.leader1?.metrics?.avgTxnsPerBlock || 0).toFixed(1) }}</td>
                                        <td class="text-end font-mono">{{ (compareData.leader2?.metrics?.avgTxnsPerBlock || 0).toFixed(1) }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div v-else-if="!compareLoading" class="text-center py-4 text-muted">
                            Enter a validator identity address above to compare side-by-side.
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { markRaw } from 'vue';
import Chart from 'chart.js/auto';
import { formatCompact, formatCompute, formatSOL, formatPubkey } from '../composables/useFormatters';
import { colors, getLineChartOptions, getBarChartOptions, getStackedBarChartOptions } from '../composables/useChartConfig';

export default {
    name: 'ValidatorDetail',
    props: { address: { type: String, required: true } },

    data() {
        return {
            activeTab: 'performance',
            tabs: [
                { key: 'performance', label: 'Performance' },
                { key: 'defi', label: 'DeFi Activity' },
                { key: 'scheduling', label: 'Block Scheduling' },
                { key: 'blocks', label: 'Recent Blocks' },
                { key: 'compare', label: 'Comparison' }
            ],
            currentRange: '7d',
            summary: {},
            defi: {},
            timeSeries: [],
            tickData: [],
            tickTotalSlots: 0,
            recentBlocks: [],
            compareAddress: '',
            compareData: null,
            compareLoading: false,
            validatorName: null,
            refreshInterval: null,
            charts: {},
            colors: colors
        };
    },

    mounted() {
        this.initPerformanceCharts();
        this.loadData();
        this.refreshInterval = setInterval(() => this.loadData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        formatCompact,
        formatCompute,
        formatSOL,
        formatPubkey,

        getCompletionClass(rate) {
            if (rate >= 90) return 'text-success';
            if (rate >= 70) return 'text-warning';
            return 'text-danger';
        },

        formatBlockTime(ts) {
            if (!ts) return '-';
            const d = new Date(ts);
            return d.toLocaleString(undefined, {
                month: 'short', day: 'numeric',
                hour: '2-digit', minute: '2-digit'
            });
        },

        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.loadData();
        },

        switchTab(tab) {
            this.activeTab = tab;
            // Lazy-load tab data
            if (tab === 'scheduling' && this.tickData.length === 0) {
                this.loadTickData();
            }
            if (tab === 'blocks' && this.recentBlocks.length === 0) {
                this.loadRecentBlocks();
            }
            // Re-render charts after tab switch (canvas may have been hidden)
            this.$nextTick(() => {
                if (tab === 'performance') {
                    Object.keys(this.charts).forEach(k => {
                        if (['blocks', 'revenue', 'tx', 'completion', 'cu'].includes(k)) {
                            this.charts[k]?.resize();
                        }
                    });
                }
                if (tab === 'defi') {
                    ['swapVolume', 'traders', 'swapCount', 'transferVolume'].forEach(k => {
                        this.charts[k]?.resize();
                    });
                }
                if (tab === 'scheduling') {
                    ['tickHeatmap', 'tickFees', 'tickNonVotes'].forEach(k => {
                        this.charts[k]?.resize();
                    });
                }
            });
        },

        initPerformanceCharts() {
            this.charts.blocks = markRaw(new Chart(this.$refs.blocksChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.charts.revenue = markRaw(new Chart(this.$refs.revenueChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ yTitle: 'SOL' })
            }));

            this.charts.tx = markRaw(new Chart(this.$refs.txChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getStackedBarChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.charts.completion = markRaw(new Chart(this.$refs.completionChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: {
                    ...getLineChartOptions({}),
                    scales: {
                        y: {
                            min: 0, max: 100,
                            title: { display: true, text: '%', color: '#9ca3af' },
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        },
                        x: {
                            ticks: { color: '#9ca3af' },
                            grid: { color: 'rgba(255,255,255,0.05)' }
                        }
                    }
                }
            }));

            this.charts.cu = markRaw(new Chart(this.$refs.cuChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => formatCompute(v) })
            }));
        },

        initDefiCharts() {
            if (this.charts.swapVolume) return;

            this.charts.swapVolume = markRaw(new Chart(this.$refs.swapVolumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => '$' + formatCompact(v) })
            }));

            this.charts.traders = markRaw(new Chart(this.$refs.tradersChart, {
                type: 'line',
                data: { labels: [], datasets: [] },
                options: getLineChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.charts.swapCount = markRaw(new Chart(this.$refs.swapCountChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.charts.transferVolume = markRaw(new Chart(this.$refs.transferVolumeChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => '$' + formatCompact(v) })
            }));
        },

        initTickCharts() {
            if (this.charts.tickHeatmap) return;

            this.charts.tickHeatmap = markRaw(new Chart(this.$refs.tickHeatmapChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: {
                    ...getBarChartOptions({ formatCallback: v => formatCompute(v) }),
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            backgroundColor: 'rgba(17, 17, 17, 0.95)',
                            titleColor: '#fff',
                            bodyColor: '#e5e7eb',
                            borderColor: 'rgba(139, 92, 246, 0.3)',
                            borderWidth: 1,
                            callbacks: {
                                label: (ctx) => `CU: ${formatCompact(ctx.raw)}`
                            }
                        }
                    }
                }
            }));

            this.charts.tickFees = markRaw(new Chart(this.$refs.tickFeesChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => formatCompact(v) })
            }));

            this.charts.tickNonVotes = markRaw(new Chart(this.$refs.tickNonVotesChart, {
                type: 'bar',
                data: { labels: [], datasets: [] },
                options: getBarChartOptions({ formatCallback: v => v.toFixed(1) })
            }));
        },

        async loadData() {
            try {
                const res = await fetch(`/analytics/api/validator-defi/${this.address}?range=${this.currentRange}`);
                const data = await res.json();

                this.summary = data.summary || {};
                this.defi = data.defi || {};
                this.timeSeries = data.timeSeries || [];

                this.updatePerformanceCharts();

                // Update DeFi charts if tab is active
                if (this.activeTab === 'defi') {
                    this.$nextTick(() => {
                        this.initDefiCharts();
                        this.updateDefiCharts();
                    });
                }

                // Reload tick data if scheduling tab is active
                if (this.activeTab === 'scheduling') {
                    this.loadTickData();
                }

                // Reload recent blocks if blocks tab is active
                if (this.activeTab === 'blocks') {
                    this.loadRecentBlocks();
                }
            } catch (e) {
                console.error('Failed to load validator data:', e);
            }
        },

        async loadTickData() {
            try {
                const res = await fetch(`/api/v1/validators/${this.address}/tick-profile?range=${this.currentRange}`);
                const json = await res.json();
                const data = json.data || json;

                this.tickData = data.ticks || [];
                this.tickTotalSlots = data.total_slots || 0;

                this.$nextTick(() => {
                    this.initTickCharts();
                    this.updateTickCharts();
                });
            } catch (e) {
                console.error('Failed to load tick data:', e);
            }
        },

        async loadRecentBlocks() {
            try {
                const res = await fetch(`/analytics/api/validator-blocks/${this.address}?range=${this.currentRange}&limit=50`);
                const data = await res.json();
                this.recentBlocks = data.blocks || [];
            } catch (e) {
                console.error('Failed to load recent blocks:', e);
            }
        },

        async loadComparison() {
            if (!this.compareAddress) return;
            this.compareLoading = true;
            try {
                const res = await fetch(`/analytics/api/validator-compare?leader1=${this.address}&leader2=${this.compareAddress}&range=${this.currentRange}`);
                this.compareData = await res.json();
            } catch (e) {
                console.error('Failed to load comparison:', e);
            } finally {
                this.compareLoading = false;
            }
        },

        updatePerformanceCharts() {
            const ts = this.timeSeries;
            if (!ts || ts.length === 0) return;
            const labels = ts.map(d => d.date);

            // Blocks chart
            this.charts.blocks.data = {
                labels,
                datasets: [{
                    label: 'Blocks',
                    data: ts.map(d => d.blocks),
                    backgroundColor: this.colors.primary,
                    borderWidth: 0
                }]
            };
            this.charts.blocks.update();

            // Revenue chart
            this.charts.revenue.data = {
                labels,
                datasets: [
                    {
                        label: 'Fees',
                        data: ts.map(d => d.fees),
                        borderColor: this.colors.primary,
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        fill: true,
                        borderWidth: 2,
                        tension: 0.4
                    },
                    {
                        label: 'Jito Tips',
                        data: ts.map(d => d.jitoTips),
                        borderColor: this.colors.jito,
                        backgroundColor: 'rgba(249, 115, 22, 0.1)',
                        fill: true,
                        borderWidth: 2,
                        tension: 0.4
                    }
                ]
            };
            this.charts.revenue.update();

            // Transaction volume (stacked: completed vs reverted)
            this.charts.tx.data = {
                labels,
                datasets: [
                    {
                        label: 'Completed',
                        data: ts.map(d => d.completedTxns),
                        backgroundColor: this.colors.completed,
                        borderWidth: 0
                    },
                    {
                        label: 'Reverted',
                        data: ts.map(d => d.revertedTxns),
                        backgroundColor: this.colors.reverted,
                        borderWidth: 0
                    }
                ]
            };
            this.charts.tx.update();

            // Completion rate
            this.charts.completion.data = {
                labels,
                datasets: [{
                    label: 'Completion %',
                    data: ts.map(d => d.completionRate),
                    borderColor: this.colors.accent,
                    backgroundColor: 'rgba(16, 247, 207, 0.1)',
                    fill: true,
                    borderWidth: 2,
                    tension: 0.4
                }]
            };
            this.charts.completion.update();

            // CU chart
            this.charts.cu.data = {
                labels,
                datasets: [{
                    label: 'Compute Units',
                    data: ts.map(d => d.computeUnits),
                    backgroundColor: this.colors.primary,
                    borderWidth: 0
                }]
            };
            this.charts.cu.update();
        },

        updateDefiCharts() {
            const ts = this.timeSeries;
            if (!ts || ts.length === 0) return;
            const labels = ts.map(d => d.date);

            if (this.charts.swapVolume) {
                this.charts.swapVolume.data = {
                    labels,
                    datasets: [{
                        label: 'Swap Volume (USD)',
                        data: ts.map(d => d.swapVolume || 0),
                        backgroundColor: this.colors.positive,
                        borderWidth: 0
                    }]
                };
                this.charts.swapVolume.update();
            }

            if (this.charts.traders) {
                this.charts.traders.data = {
                    labels,
                    datasets: [
                        {
                            label: 'Traders',
                            data: ts.map(d => d.traders || 0),
                            borderColor: this.colors.secondary,
                            backgroundColor: 'rgba(236, 72, 153, 0.1)',
                            fill: true,
                            borderWidth: 2,
                            tension: 0.4
                        },
                        {
                            label: 'Signers',
                            data: ts.map(d => d.signers || 0),
                            borderColor: this.colors.accent,
                            backgroundColor: 'rgba(16, 247, 207, 0.05)',
                            fill: true,
                            borderWidth: 2,
                            tension: 0.4
                        }
                    ]
                };
                this.charts.traders.update();
            }

            if (this.charts.swapCount) {
                this.charts.swapCount.data = {
                    labels,
                    datasets: [{
                        label: 'Swap Count',
                        data: ts.map(d => d.swapCount || 0),
                        backgroundColor: this.colors.accent,
                        borderWidth: 0
                    }]
                };
                this.charts.swapCount.update();
            }

            if (this.charts.transferVolume) {
                this.charts.transferVolume.data = {
                    labels,
                    datasets: [{
                        label: 'Transfer Volume (USD)',
                        data: ts.map(d => d.transferVolume || 0),
                        backgroundColor: this.colors.primary,
                        borderWidth: 0
                    }]
                };
                this.charts.transferVolume.update();
            }
        },

        updateTickCharts() {
            if (!this.tickData.length) return;
            const labels = this.tickData.map(t => `T${t.tick}`);
            const maxCu = Math.max(...this.tickData.map(t => t.avg_cu));

            // CU heatmap-style bar chart with gradient coloring
            if (this.charts.tickHeatmap) {
                this.charts.tickHeatmap.data = {
                    labels,
                    datasets: [{
                        label: 'Avg CU',
                        data: this.tickData.map(t => t.avg_cu),
                        backgroundColor: this.tickData.map(t => {
                            const intensity = maxCu > 0 ? t.avg_cu / maxCu : 0;
                            // Gradient from dark purple to bright cyan
                            const r = Math.round(139 * (1 - intensity) + 16 * intensity);
                            const g = Math.round(92 * (1 - intensity) + 247 * intensity);
                            const b = Math.round(246 * (1 - intensity) + 207 * intensity);
                            return `rgba(${r}, ${g}, ${b}, ${0.4 + intensity * 0.6})`;
                        }),
                        borderWidth: 0
                    }]
                };
                this.charts.tickHeatmap.update();
            }

            // Priority fees by tick
            if (this.charts.tickFees) {
                this.charts.tickFees.data = {
                    labels,
                    datasets: [{
                        label: 'Avg Priority Fees',
                        data: this.tickData.map(t => t.avg_priority_fees),
                        backgroundColor: this.colors.jito,
                        borderWidth: 0
                    }]
                };
                this.charts.tickFees.update();
            }

            // Non-votes by tick
            if (this.charts.tickNonVotes) {
                this.charts.tickNonVotes.data = {
                    labels,
                    datasets: [{
                        label: 'Avg Non-Votes',
                        data: this.tickData.map(t => t.avg_non_votes),
                        backgroundColor: this.colors.primary,
                        borderWidth: 0
                    }]
                };
                this.charts.tickNonVotes.update();
            }
        },

        exportPerformanceCSV() {
            const headers = ['Date', 'Blocks', 'Txns', 'Completed', 'Reverted', 'Completion%', 'Fees', 'JitoTips', 'Revenue', 'CU'];
            const rows = this.timeSeries.map(r => [
                r.date, r.blocks, r.txns, r.completedTxns, r.revertedTxns,
                r.completionRate, r.fees.toFixed(6), r.jitoTips.toFixed(6),
                r.revenue.toFixed(6), r.computeUnits
            ]);
            const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `validator-${this.address.slice(0, 8)}-performance.csv`;
            a.click();
            URL.revokeObjectURL(url);
        }
    }
};
</script>

<style scoped>
.validator-tabs {
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    padding-bottom: 1rem;
}

.validator-tabs .btn-group {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.compare-input-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.compare-grid table {
    width: 100%;
}

@media (max-width: 768px) {
    .compare-input-row {
        flex-direction: column;
        align-items: stretch;
    }
    .compare-input-row input {
        max-width: 100% !important;
    }
}
</style>
